import { X } from "lucide-react";
import { useEffect } from "react";

type ApplicationStatus = "pending" | "approved" | "rejected" | "in-review" | "in-interview";

export default function UserApplicationModal({
    data,
    onClose,
    lang = "en",
}: {
    data: any
    onClose: () => void;
    lang?: "en" | "id";
}) {
    useEffect(() => {
        // disable scroll saat modal terbuka
        document.body.style.overflow = "hidden";
        return () => {
            // enable kembali saat modal ditutup
            document.body.style.overflow = "auto";
        };
    }, []);
    if (!data) return null;

    const status = data.status as ApplicationStatus;

    const statusLabel: Record<ApplicationStatus, string> = {
    approved: lang === "id" ? "Diterima ✓" : "Approved ✓",
    rejected: lang === "id" ? "Ditolak ✕" : "Rejected ✕",
    pending: lang === "id" ? "Dalam Peninjauan ⏳" : "In Review ⏳",
    "in-review": lang === "id" ? "Direview ⏳" : "Reviewed ⏳",
    "in-interview": lang === "id" ? "Interview ⏳" : "Interviewed ⏳",
    };

    const statusColor: Record<ApplicationStatus, string> = {
    approved: "bg-green-100 text-green-700",
    rejected: "bg-red-100 text-red-700",
    pending: "bg-blue-100 text-blue-700",
    "in-review": "bg-yellow-100 text-yellow-700",
    "in-interview": "bg-purple-100 text-purple-700",
    };


    const formatDate = (date: string) =>
        new Date(date).toLocaleDateString(lang === "id" ? "id-ID" : "en-US", {
            year: "numeric",
            month: "long",
            day: "numeric",
        });

    return (
        <div className="fixed inset-0 bg-black/50 flex justify-center items-center z-50 px-4">
            <div className="bg-white w-full max-w-3xl rounded-2xl p-6 relative max-h-[90vh] overflow-y-auto animate-fadeIn">

                <button
                    className="absolute top-4 right-4 text-gray-600 hover:text-black"
                    onClick={onClose}
                >
                    <X size={24} />
                </button>

                <div className="flex items-center gap-4">
                    <img
                        src={data.companyLogo || "https://placehold.co/100x100"}
                        alt="Company Logo"
                        className="w-20 h-20 rounded-xl object-cover"
                    />
                    <div>
                        <h2 className="text-xl font-semibold">{data.positionApplied}</h2>
                        <p className="text-gray-600">{data.companyName}</p>
                        <p className="text-gray-600">{lang === "id" ? "Tipe Pekerjaan: " : "Working Type: "}{data.workingType}</p>
                        <p className="text-gray-600">{lang === "id" ? "Jenis Pekerjaan: " : "Job Type: "}{data.jobType}</p>
                        <p className="text-sm text-gray-500 mt-1">
                            {lang === "id" ? "Dilamar pada" : "Applied on"}{" "}
                            <span className="font-medium">{formatDate(data.appliedDate)}</span>
                        </p>
                    </div>
                </div>

                <div className="my-2">
                    <span className="text-md mt-1">
                        Location: {data.country}, {data.state}, {data.city}
                    </span>
                </div>


                <div>
                    <span className={`px-3 py-1 rounded-lg text-sm font-medium ${statusColor[status]}`}>
                        {statusLabel[status]}
                    </span>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 my-4">
                    <InfoBox label="Industry" value={data.industry} />
                    <InfoBox label="Degree" value={data.degree} />
                    <InfoBox label="Year of Experience" value={data.expYear} />
                    <InfoBox label="Level of Experience" value={data.expLevel} /><InfoBox
                        label="Minimal Salary"
                        value={
                            data?.showSalary
                                ? data?.minSalary != null
                                    ? "IDR " + data.minSalary.toLocaleString()
                                    : "-"
                                : lang === "id"
                                    ? "Gaji Dirahasiakan"
                                    : "Salary Undisclosed"
                        }
                    />
                    <InfoBox
                        label="Maximum Salary"
                        value={
                            data?.showSalary
                                ? data?.maxSalary != null
                                    ? "IDR " + data.maxSalary.toLocaleString()
                                    : "-"
                                : lang === "id"
                                    ? "Gaji Dirahasiakan"
                                    : "Salary Undisclosed"
                        }
                    />

                </div>

                <div className="space-y-3">
                    <p className="font-semibold text-lg">{lang === "id" ? "Deskripsi Pekerjaan" : "Job Description"}</p>
                    <p className="font-semibold my-2">{lang === "id" ? "Tanggung Jawab" : "Responsibilities"}</p>
                    <div
                        className="text-gray-700 leading-relaxed text-md [&_ul]:list-disc [&_ul]:ms-5 [&_ol]:list-decimal [&_ol]:ms-5 [&_p]:my-2"
                        dangerouslySetInnerHTML={{
                            __html: data.responsibility || "-",
                        }}
                    />
                    <p className="font-semibold">{lang === "id" ? "Persyaratan" : "Requirements"}</p>
                    <div
                        className="text-gray-700 leading-relaxed text-md [&_ul]:list-disc [&_ul]:ms-5 [&_ol]:list-decimal [&_ol]:ms-5 [&_p]:my-2"
                        dangerouslySetInnerHTML={{
                            __html: data.requirement || "-",
                        }}
                    />
                    {data.benefit && data.benefit.length > 0 && (
                        <section className="mt-6">
                            <h3 className="font-semibold text-lg mb-3">
                                {lang === "id" ? "Manfaat" : "Benefits"}
                            </h3>

                            <div className="flex flex-wrap gap-2">
                                {data.benefit.map((benefit: { id: number; name: string }) => (
                                    <span
                                        key={benefit.id}
                                        className="px-3 py-1 rounded-full bg-primary/10 text-primary text-sm font-medium"
                                    >
                                        {benefit.name}
                                    </span>
                                ))}
                            </div>
                        </section>
                    )}
                    {data.skills && data.skills.length > 0 && (
                        <section className="mt-6">
                            <h3 className="font-semibold text-lg mb-3">{lang === "id" ? "Keahlian" : "Skills"}</h3>

                            <div className="flex flex-wrap gap-2">
                                {data.skills.map((skill: { id: number; name: string }) => (
                                    <span
                                        key={skill.id}
                                        className="px-3 py-1 rounded-full bg-primary/10 text-primary text-sm font-medium"
                                    >
                                        {skill.name}
                                    </span>
                                ))}
                            </div>
                        </section>
                    )}



                </div>

                <div className="mt-6 text-right">
                    <a
                        href={data.cv}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary underline font-medium hover:opacity-80"
                    >
                        {lang === "id" ? "Lihat CV Anda" : "View Your CV"}
                    </a>
                </div>
            </div>
        </div>
    );
}

function InfoBox({ label, value }: any) {
    return (
        <div className="border rounded-lg p-3 text-sm">
            <p className="text-gray-500 text-sm font-medium">{label}</p>
            <p className="text-gray-800 mt-1 text-base">{value || "-"}</p>
        </div>
    );
}

